import { useMemo } from 'react';
import React from 'react';
import { Field } from '@tw/tw-component-library';
import {
  EnhancedGroupedSection,
  FormSchemaConfig,
} from '../../types/form/schema.types';
import { FormState } from '../../types';
import { FileDisplay } from '../../components/FileDisplay';

// ============= Component =============
export const useDubaiPaymentFromOfficeAccountPaymentConfirmationSchema = (
  formState: FormState
): FormSchemaConfig => {
  return useMemo<FormSchemaConfig>(() => {
    const isEditAllowed =
      formState.data?.userAssignedSections?.includes(
        'dubai_payment_from_office_account_payment_confirmation'
      ) === true;

    // Create the main section that matches EnhancedGroupedSection type
    const paymentConfirmationSection: EnhancedGroupedSection = {
      layout: 'single',
      isGrouped: true,
      isGroupedAccordion: false,
      sections: [
        {
          title: {
            text: 'Payment Confirmation',
          },
          layout: 'single',
          fields: [
            {
              type: 'radio',
              name: 'paymentFromOfficePaymentConfirmation',
              label: 'Has this payment been actioned?',
              options: [
                {
                  value: 'yes',
                  label: 'Yes',
                  disabled: false,
                },
                {
                  value: 'no',
                  label: 'No',
                  disabled: false,
                },
              ],
              required: true,
              validate: (value) =>
                !value
                  ? 'Please select whether this payment has been actioned'
                  : undefined,
              disabled:
                formState.completedSections?.includes(
                  'dubai_payment_from_office_account_payment_confirmation'
                ) || !isEditAllowed,
            },
            {
              type: 'textArea',
              name: 'paymentFromOfficePaymentRejectionDetails',
              label: 'Rejection Details',
              required:
                formState.data.paymentFromOfficePaymentConfirmation === 'no',
              validate: (value) => {
                const isRequired =
                  formState.data.paymentFromOfficePaymentConfirmation === 'no';
                if (!isRequired) return undefined;
                return !value
                  ? 'Please enter the rejection details'
                  : undefined;
              },
              disabled:
                formState.completedSections?.includes(
                  'dubai_payment_from_office_account_payment_confirmation'
                ) || !isEditAllowed,
              dependencies: [
                {
                  type: 'show',
                  logicalOperator: 'AND',
                  conditions: [
                    {
                      field: 'paymentFromOfficePaymentConfirmation',
                      operator: 'equals',
                      value: 'no',
                    },
                  ],
                },
              ],
            },
            // Show existing files when form is completed
            ...(formState.completedSections?.includes(
              'dubai_payment_from_office_account_payment_confirmation'
              ) &&
             formState.data.paymentFromOfficePaymentUploadDocumentation
              ? [
                  {
                    type: 'customComponent' as const,
                    customComponent: React.createElement(FileDisplay, {
                      fileName: formState.data.paymentFromOfficePaymentUploadDocumentation,
                      workflowId: formState.data?.id,
                    }),
                    dependencies: [
                      {
                        type: 'show' as const,
                        logicalOperator: 'AND' as const,
                        conditions: [
                          {
                            field: 'paymentFromOfficePaymentConfirmation',
                            operator: 'equals' as const,
                            value: 'yes',
                          },
                        ],
                      },
                    ],
                    name: 'payment_upload_documentation_display',
                    label: 'Supporting Documentation:',
                  },
                ]
              : []),
            // Show existing files when rejected (but still allow uploads)
            ...(formState.isRejected &&
            formState.data.paymentFromOfficePaymentUploadDocumentation
              ? [
                  {
                    type: 'customComponent' as const,
                    customComponent: React.createElement(FileDisplay, {
                      fileName: formState.data.paymentFromOfficePaymentUploadDocumentation,
                      workflowId: formState.data?.id,
                    }),
                    dependencies: [
                      {
                        type: 'show' as const,
                        logicalOperator: 'AND' as const,
                        conditions: [
                          {
                            field: 'paymentFromOfficePaymentConfirmation',
                            operator: 'equals' as const,
                            value: 'yes',
                          },
                        ],
                      },
                    ],
                    name: 'payment_upload_documentation_display_rejected',
                    label: 'Previously Uploaded Supporting Documentation:',
                  },
                ]
              : []),
            // Always show file upload field (unless form is completed and not rejected)
            ...(!formState.completedSections?.includes(
              'dubai_payment_from_office_account_payment_confirmation'
            ) || formState.isRejected
              ? [
                  {
                    type: 'file' as const,
                    name: 'paymentFromOfficePaymentUploadDocumentation',
                    label: formState.isRejected
                      ? 'Upload Additional Supporting Documentation (if needed):'
                      : 'Upload supporting Documentation',
                    tooltip: 'Document Viewer only supports: .pdf',
                    extras: {
                      file: {
                        multiple: true,
                      },
                    } as Field['extras'],
                    disabled:
                      (formState.completedSections?.includes(
                        'dubai_payment_from_office_account_payment_confirmation'
                      ) &&
                        !formState.isRejected) ||
                      !isEditAllowed,
                    dependencies: [
                      {
                        type: 'show' as const,
                        logicalOperator: 'AND' as const,
                        conditions: [
                          {
                            field: 'paymentConfirmation',
                            operator: 'equals' as const,
                            value: 'yes',
                          },
                        ],
                      },
                    ],
                  },
                ]
              : []),
            {
              type: 'textArea',
              name: 'paymentFromOfficePaymentComments',
              label: 'Any other comments',
              disabled:
                formState.completedSections?.includes(
                  'dubai_payment_from_office_account_payment_confirmation'
                ) || !isEditAllowed,
              dependencies: [
                {
                  type: 'show',
                  logicalOperator: 'AND',
                  conditions: [
                    {
                      field: 'paymentFromOfficePaymentConfirmation',
                      operator: 'equals',
                      value: 'yes',
                    },
                  ],
                },
              ],
            },
          ],
        },
      ],
      extras: { grouped: true, evenSpacing: true },
    };

    return {
      sections: [paymentConfirmationSection],
      dependencies: {
        showWhen: [
          {
            field: 'dubai_payment_from_office_account_approver',
          },
        ],
      },
    };
  }, [formState]);
};
